import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";
import { trackPageView } from "@/lib/analytics";
import { AuthProvider } from "@/context/AuthContext";
import RequireSubscriber from "@/components/RequireSubscriber";
import SubscriberHome from "@/pages/SubscriberHome";
import BeginJourney from "@/pages/BeginJourney";
import SignIn from "@/pages/SignIn";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import TheUnsaid from "@/pages/TheUnsaid";
import TheUnsaidReveal from "@/pages/TheUnsaidReveal";
import EmotionalXray from "@/pages/EmotionalXray";
import TodayPlan from "@/pages/TodayPlan";
import FutureSelf from "@/pages/FutureSelf";
import PurposeEngine from "@/pages/PurposeEngine";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import JournalPage from "@/pages/JournalPage";
import ProgressPage from "@/pages/ProgressPage";
import JourneyHistory from "@/pages/JourneyHistory";
import NotFound from "@/pages/NotFound";
// Retired short onboarding kept for rollback: import DiscoveryConversation from "@/pages/DiscoveryConversation";
import Home from "@/pages/Home";
import XrayReveal from "@/pages/XrayReveal";
import MemoryPage from "@/pages/MemoryPage";
import Journey from "@/pages/Journey";
import OnboardingComplete from "@/pages/OnboardingComplete";
import AdminInquiries from "@/pages/AdminInquiries";
import AdminGoLive from "@/pages/AdminGoLive";
import Discover from "@/pages/Discover";
import DiscoverUnlock from "@/pages/DiscoverUnlock";

// FioNova Web — onboarding/orientation flow (Phase 1).
// The paywall is the boundary between onboarding and the full app; the
// post-subscription application is a later, separately-sourced phase.
// Sends one GA4 page_view per SPA navigation (config has send_page_view:false).
// Legacy paywall URL → the single onboarding trial offer (or the right home for this user).
function TrialOfferRedirect() {
  const { user, loading } = useAuth();
  if (loading || user === undefined) return null;
  if (user?.entitled) return <Navigate to="/app" replace />;
  return <Navigate to={user?.has_discovery ? "/discovery/reveal#trial" : "/"} replace />;
}

function RouteAnalytics() {
  const location = useLocation();
  useEffect(() => { trackPageView(); }, [location.pathname, location.search]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <RouteAnalytics />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/discovery/reveal" element={<XrayReveal />} />
            <Route path="/discovery/xray" element={<XrayReveal />} />
            <Route path="/start" element={<Navigate to="/" replace />} />
            <Route path="/begin" element={<BeginJourney />} />
            <Route path="/login" element={<SignIn />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/onboarding/hello" element={<Navigate to="/" replace />} />
            <Route path="/onboarding/intro/:step" element={<Navigate to="/" replace />} />
            <Route path="/onboarding/setup" element={<Navigate to="/" replace />} />
            <Route path="/welcome" element={<Navigate to="/" replace />} />
            <Route path="/the-unsaid" element={<RequireSubscriber><TheUnsaid /></RequireSubscriber>} />
            <Route path="/the-unsaid/reveal" element={<RequireSubscriber><TheUnsaidReveal /></RequireSubscriber>} />
            <Route path="/emotional-xray" element={<RequireSubscriber><EmotionalXray /></RequireSubscriber>} />
            <Route path="/today-plan" element={<RequireSubscriber><TodayPlan /></RequireSubscriber>} />
            <Route path="/future-self" element={<RequireSubscriber><FutureSelf /></RequireSubscriber>} />
            <Route path="/purpose" element={<RequireSubscriber><PurposeEngine /></RequireSubscriber>} />
            <Route path="/complete" element={<Navigate to="/" replace />} />
            <Route path="/journey" element={<Journey />} />
            <Route path="/journey/complete" element={<OnboardingComplete />} />
            <Route path="/admin/inquiries" element={<AdminInquiries />} />
            <Route path="/admin/go-live" element={<AdminGoLive />} />
            <Route path="/discover" element={<Discover />} />
            <Route path="/discover/unlock" element={<DiscoverUnlock />} />
            <Route path="/subscribe" element={<TrialOfferRedirect />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/app" element={<RequireSubscriber><SubscriberHome /></RequireSubscriber>} />
            <Route path="/journal" element={<RequireSubscriber><JournalPage /></RequireSubscriber>} />
            <Route path="/memory" element={<RequireSubscriber><MemoryPage /></RequireSubscriber>} />
            <Route path="/progress" element={<RequireSubscriber><ProgressPage /></RequireSubscriber>} />
            <Route path="/history/:kind" element={<RequireSubscriber><JourneyHistory /></RequireSubscriber>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
